import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Warehouse } from "lucide-react";
import {
  PageHeader,
  StatCard,
  Card,
  Select,
  fmtNum,
} from "../ui/caliper";
import { useWarehouse } from "../Layout/WarehouseContext";

const SEGMENTS = [
  {
    id: "cheEngage",
    label: "CHE / Engage",
    desc: "Community Health Engagement — via eClinics",
    path: "/cheOrders",
  },
  { id: "b2c", label: "B2C", desc: "Direct consumer", path: "/b2cOrders" },
  // B2B Partners screens disabled — hide the dashboard segment card.
  // {
  //   id: "b2b",
  //   label: "B2B Partners",
  //   desc: "Partner clinics & hospitals",
  //   path: "/b2bOrders",
  // },
];

const STAGES = [
  { key: "enquiry", label: "Enquiry", color: "var(--neutral-200)", fg: "text-fg-muted" },
  { key: "received", label: "Received", color: "var(--ink-500)", fg: "text-ink-700" },
  { key: "confirm", label: "Confirm", color: "var(--green-500)", fg: "text-green-700" },
  { key: "packed", label: "Packed", color: "var(--amber-500)", fg: "text-amber-700" },
  { key: "cancelled", label: "Cancelled", color: "var(--red-500)", fg: "text-red-700" },
];

const RANGE_LABEL = {
  Today: "Today",
  "1week": "Last 7 days",
  "1month": "Last 30 days",
  "6months": "Last 6 months",
};

const FunnelBar = ({ counts, total }) => {
  if (!total) return <span className="text-fg-subtle text-t-xs">—</span>;
  const segs = STAGES.map((s) => ({ ...s, c: counts[s.key] || 0 })).filter((s) => s.c > 0);
  return (
    <div className="flex h-2 rounded-cb-xs overflow-hidden bg-sunken w-full max-w-[220px]">
      {segs.map((s) => (
        <div
          key={s.key}
          title={`${s.label}: ${s.c}`}
          style={{ width: `${(s.c / total) * 100}%`, background: s.color }}
        />
      ))}
    </div>
  );
};

const Dashboard = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  /* The app-wide store selection (topbar). This screen shows figures for exactly
     one store and nothing else: with no store selected, and with "All stores"
     selected, it fetches NOTHING.

     "All stores" is deliberately included in that. The endpoint behind this screen
     is scoped by state + circle, so an estate-wide request is not something it can
     answer — the closest it could do is the signed-in user's own region, which
     would sit under an "all stores" heading while being one region's numbers. A
     blank screen is better than a total that is quietly wrong. */
  const { code: wh, isAll, warehouses, loading: storesLoading } = useWarehouse();
  const scopeReady = Boolean(wh);
  const store = warehouses.find((w) => String(w.id) === String(wh));
  /* The region actually queried, as two scalars. Derived rather than read inline in
     the fetch because `warehouses` arrives asynchronously: with the row read inside
     fetchDashboardData and absent from the deps below, the first fetch used the
     login region and NOTHING re-fired once the store's row landed — so selecting a
     store appeared to change nothing. Depending on these two values fixes that. */
  const scopeState = store?.stateCode || localStorage.getItem("userState") || "OD";
  const scopeCircle = store?.circleCode || localStorage.getItem("circleCode") || "N";

  const getDateRange = (range) => {
    const today = new Date();
    let from = new Date(today);
    switch (range) {
      case "Today":
        break;
      case "1week":
        from.setDate(today.getDate() - 6);
        break;
      case "1month":
        from.setMonth(today.getMonth() - 1);
        break;
      case "6months":
        from.setMonth(today.getMonth() - 6);
        break;
      default:
        break;
    }
    return {
      fromDate: from.toISOString().split("T")[0],
      toDate: today.toISOString().split("T")[0],
    };
  };

  const [dateRange, setDateRange] = useState("1week");
  const [fromDate, setFromDate] = useState(getDateRange("1week").fromDate);
  const [toDate, setToDate] = useState(getDateRange("1week").toDate);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      /* This endpoint scopes by state + circle, not by warehouse — so the selected
         store scopes it as far as it can: through that store's own state and
         circle. Several stores can share a region, so these are the numbers for the
         REGION the chosen store sits in, not for the store alone. With "All stores"
         (or no store row loaded yet) it falls back to the signed-in user's own
         state / circle, which is the previous behaviour. */
      const { data } = await axios.get(`${apiUrl}dashboard/`, {
        params: {
          state: scopeState,
          fromDate,
          toDate,
          circleCode: scopeCircle,
        },
      });
      setDashboardData(data?.successObject || {});
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const r = getDateRange(dateRange);
    setFromDate(r.fromDate);
    setToDate(r.toDate);
  }, [dateRange]);

  useEffect(() => {
    /* No single store in scope — nothing is fetched. `loading` goes false so the
       screen settles on its prompt instead of spinning forever. */
    if (!scopeReady) {
      setDashboardData({});
      setLoading(false);
      return;
    }
    /* A store IS selected but its row hasn't arrived yet, so its region is unknown.
       Waiting beats firing once with the login region and correcting a moment
       later — that flashes another region's numbers under this store's name. */
    if (!store && storesLoading) {
      setLoading(true);
      return;
    }
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, scopeReady, scopeState, scopeCircle, store, storesLoading]);

  /* per-segment counts */
  const segRows = SEGMENTS.map((s) => {
    const c = dashboardData[s.id] || {};
    const counts = STAGES.reduce((acc, st) => {
      acc[st.key] = Number(c[st.key]) || 0;
      return acc;
    }, {});
    counts.total = STAGES.reduce((sum, st) => sum + counts[st.key], 0);
    return { ...s, ...counts };
  });

  /* aggregate KPIs */
  const agg = STAGES.reduce((acc, st) => {
    acc[st.key] = segRows.reduce((sum, r) => sum + r[st.key], 0);
    return acc;
  }, {});
  const totalOrders = segRows.reduce((sum, r) => sum + r.total, 0);
  /* Fetched, and the honest answer is nothing. Without saying so, a grid of dashes
     reads as a broken screen — and most regions genuinely have no orders. */
  const loadedButEmpty = scopeReady && !loading && totalOrders === 0;

  const today = new Date();
  const dateLabel = today.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const kpis = [
    { label: "Total orders", value: totalOrders, accent: "var(--fg-strong)", sub: `${RANGE_LABEL[dateRange]} · 3 segments` },
    { label: "Enquiries", value: agg.enquiry, accent: "var(--fg-strong)", sub: "Awaiting first action" },
    { label: "Received", value: agg.received, accent: "var(--ink-700)", sub: "In the pipeline" },
    { label: "Confirmed", value: agg.confirm, accent: "var(--green-700)", sub: "Ready to pack" },
    { label: "Cancelled", value: agg.cancelled, accent: "var(--red-700)", sub: "Across all segments" },
  ];

  return (
    <div className="flex flex-col gap-4 cb-fade-in">
      <PageHeader
        title="Operations overview"
        /* The scope is named in the subtitle: a number without a stated scope is
           the thing that gets misread. */
        sub={
          scopeReady
            ? `${dateLabel} · ${fromDate} → ${toDate}${store?.name ? ` · ${store.name}` : ""}`
            : `${dateLabel} · ${isAll ? "all stores — pick one" : "no store selected"}`
        }
        actions={
          <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="Today">Today</option>
            <option value="1week">Last 7 days</option>
            <option value="1month">Last 30 days</option>
            <option value="6months">Last 6 months</option>
          </Select>
        }
      />

      {!scopeReady ? (
        /* A head with no store chosen. Nothing has been fetched, so say why rather
           than render a grid of dashes that looks like a business with no orders. */
        <Card className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <Warehouse size={26} strokeWidth={1.5} className="text-fg-subtle" />
          <div className="text-t-base font-semibold text-fg-strong">
            {isAll ? "Pick a single store" : "Select a store to begin"}
          </div>
          <div className="text-t-sm text-fg-muted max-w-[440px]">
            {isAll
              ? "“All stores” is selected. This overview reports on one store at a time — choose a single store in the Store control in the top bar."
              : "Choose a store from the Store control in the top bar and this overview loads for it. Nothing is fetched until then."}
          </div>
        </Card>
      ) : (
        <>
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <StatCard
            key={k.label}
            label={k.label}
            value={loading ? "—" : fmtNum(k.value)}
            sub={k.sub}
            accent={loading ? "var(--fg-subtle)" : k.accent}
          />
        ))}
      </div>

      {loadedButEmpty && (
        <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-cb-sm bg-amber-50 text-amber-700 text-t-sm font-semibold">
          <span>
            No orders in this window for {store?.name || "this store"} (region {scopeState} /{" "}
            {scopeCircle}).
            Widen the date range, or check another store — order volume is per region, and not every
            region has traffic.
          </span>
        </div>
      )}

      {/* Funnel by segment */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 border-b border-hairline-subtle">
          <span className="cb-eyebrow">Order funnel · by segment</span>
          <div className="flex flex-wrap items-center gap-3">
            {STAGES.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-t-xs text-fg-muted">
                <span className="w-2 h-2 rounded-[2px]" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="cb-th text-left px-3.5 py-2 bg-surface-subtle border-b border-hairline">
                Segment
              </th>
              <th className="cb-th text-right px-3 py-2 bg-surface-subtle border-b border-hairline w-[70px]">
                Total
              </th>
              <th className="cb-th text-left px-3 py-2 bg-surface-subtle border-b border-hairline">
                Funnel
              </th>
              {STAGES.map((s) => (
                <th
                  key={s.key}
                  className="cb-th text-right px-3 py-2 bg-surface-subtle border-b border-hairline w-[84px]"
                >
                  {s.label}
                </th>
              ))}
              <th className="bg-surface-subtle border-b border-hairline w-[32px]" />
            </tr>
          </thead>
          <tbody>
            {segRows.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(r.path)}
                className="border-b border-hairline-subtle last:border-0 hover:bg-surface-hover cursor-pointer transition-colors"
              >
                <td className="px-3.5 py-2.5">
                  <div className="text-t-md font-semibold text-fg-strong">{r.label}</div>
                  <div className="text-[10px] text-fg-muted mt-0.5">{r.desc}</div>
                </td>
                <td className="px-3 text-right font-mono tabular-nums text-t-base font-semibold text-fg-strong">
                  {loading ? "—" : fmtNum(r.total)}
                </td>
                <td className="px-3 min-w-[120px]">
                  {loading ? (
                    <div className="cb-skeleton h-2 w-[180px] max-w-full rounded-cb-xs" />
                  ) : (
                    <FunnelBar counts={r} total={r.total} />
                  )}
                </td>
                {STAGES.map((s) => (
                  <td
                    key={s.key}
                    className={`px-3 text-right font-mono tabular-nums text-t-sm ${
                      r[s.key] ? s.fg : "text-fg-subtle"
                    } ${r[s.key] && (s.key === "received" || s.key === "confirm") ? "font-semibold" : ""}`}
                  >
                    {loading ? "—" : r[s.key] || "—"}
                  </td>
                ))}
                <td className="px-2 text-right">
                  <ChevronRight size={13} strokeWidth={1.75} className="text-fg-subtle" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
